// DashboardMedecin.js
import React, { useMemo, useState, useEffect,useCallback,useRef } from "react";


import { API_BASE } from "../config";


export function MonCabinetMedecin({ user }) {
  const token = user?.token;

  const [loading, setLoading] = useState(false);
  const [loadingPwd, setLoadingPwd] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [showPasswordForm, setShowPasswordForm] = useState(false);

  // ✅ confirmation password for profile save
  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  const confirmPwdRef = useRef(null);
  const [toast, setToast] = useState({ type: "", msg: "" });
  
  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast({ type: "", msg: "" }), 3200);
  };

  const [profile, setProfile] = useState({
    prenom: "",
    nom: "",
    email: "",
    telephone: "",
    adresse_cabinet: "",
    code_postal_cabinet: "",
    ville_cabinet: "",
  });

  const [form, setForm] = useState({
    email: "",
    telephone: "",
    adresse_cabinet: "",
    code_postal_cabinet: "",
    ville_cabinet: "",
  });

  const [pwd, setPwd] = useState({
    mot_de_passe_actuel: "",
    nouveau_mot_de_passe: "",
    confirmation: "",
  });

  const headers = useMemo(
    () => ({
      "Content-Type": "application/json",
      Authorization: `Bearer ${token || ""}`,
    }),
    [token]
  );

  const resetMessages = () => {
    setError("");
    setSuccess("");
  };

  const flashSuccess = (msg) => {
    setSuccess(msg);
    window.setTimeout(() => setSuccess(""), 2500);
  };

  const fetchMe = useCallback(async () => {
    if (!token) return;
    setLoading(true);



    try {
      const res = await fetch(`${API_BASE}/medecin/me`, { headers });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.msg || "Erreur chargement profil");

      const p = {
        prenom: data.item?.prenom || "",
        nom: data.item?.nom || "",
        email: data.item?.email || "",
        telephone: data.item?.telephone || "",
        adresse_cabinet: data.item?.adresse_cabinet || "",
        code_postal_cabinet: data.item?.code_postal_cabinet || "",
        ville_cabinet: data.item?.ville_cabinet || "",
      };

      setProfile(p);
      setForm({
        email: p.email,
        telephone: p.telephone,
        adresse_cabinet: p.adresse_cabinet,
        code_postal_cabinet: p.code_postal_cabinet,
        ville_cabinet: p.ville_cabinet,
      });

      setConfirmSaveOpen(false);
      setConfirmPassword("");
    } catch (e) {
      setError(e?.message || "Impossible de contacter le serveur.");
    } finally {
      setLoading(false);
    }
  }, [headers, token]);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const isDirty =
    form.email !== profile.email ||
    form.telephone !== profile.telephone ||
    form.adresse_cabinet !== profile.adresse_cabinet ||
    form.code_postal_cabinet !== profile.code_postal_cabinet ||
    form.ville_cabinet !== profile.ville_cabinet;

  const cancelProfileEdits = () => {
    resetMessages();
    setForm({
      email: profile.email,
      telephone: profile.telephone,
      adresse_cabinet: profile.adresse_cabinet,
      code_postal_cabinet: profile.code_postal_cabinet,
      ville_cabinet: profile.ville_cabinet,
    });
    setConfirmSaveOpen(false);
    setConfirmPassword("");
  };

  const validateProfile = () => {
    const email = (form.email || "").trim();
    if (!email) {
      setError("Email requis.");
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Email invalide.");
      return false;
    }

    // ✅ si code postal saisi, vérifier format FR simple
    const cp = (form.code_postal_cabinet || "").trim();
    if (cp && !/^\d{5}$/.test(cp)) {
      setError("Code postal invalide (5 chiffres).");
      return false;
    }

    return true;
  };

  // ✅ Step 1: clic Enregistrer => ouverture confirmation mdp
  const requestSave = () => {
    resetMessages();
    if (!isDirty) return;
    if (!validateProfile()) return;

    setConfirmSaveOpen(true);
    window.setTimeout(() => confirmPwdRef.current?.focus?.(), 50);
  };

  // ✅ Step 2: confirme mdp => sauvegarde
  const confirmAndSaveProfile = async () => {
    if (!token) return;
    resetMessages();
    if (!validateProfile()) return;

    const pwdValue = (confirmPassword || "").trim();
    if (!pwdValue) {
      setError("Veuillez saisir votre mot de passe pour valider.");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/medecin/me`, {
        method: "PUT",
        headers,
        body: JSON.stringify({
          email: (form.email || "").trim(),
          telephone: (form.telephone || "").trim(),
          adresse_cabinet: (form.adresse_cabinet || "").trim(),
          code_postal_cabinet: (form.code_postal_cabinet || "").trim(),
          ville_cabinet: (form.ville_cabinet || "").trim(),
          mot_de_passe: pwdValue,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.msg || "Erreur mise à jour");

      flashSuccess("Informations mises à jour");
      setConfirmSaveOpen(false);
      setConfirmPassword("");
      await fetchMe();
    } catch (e) {
      setError(e?.message || "Impossible de contacter le serveur.");
    } finally {
      setLoading(false);
    }
  };

  const closePwd = () => {
    setPwd({ mot_de_passe_actuel: "", nouveau_mot_de_passe: "", confirmation: "" });
    setShowPasswordForm(false);
    resetMessages();
  };

  const validatePwd = () => {
    if (!pwd.mot_de_passe_actuel) {
      setError("Veuillez saisir votre mot de passe actuel.");
      return false;
    }
    if (!pwd.nouveau_mot_de_passe) {
      setError("Veuillez saisir un nouveau mot de passe.");
      return false;
    }
    if (pwd.nouveau_mot_de_passe.length < 8) {
      setError("Mot de passe trop court (min 8 caractères).");
      return false;
    }
    if (pwd.nouveau_mot_de_passe !== pwd.confirmation) {
      setError("La confirmation ne correspond pas.");
      return false;
    }
    return true;
  };

  const changePassword = async () => {
    if (!token) return;
    resetMessages();
    if (!validatePwd()) return;

    setLoadingPwd(true);
    try {
      const res = await fetch(`${API_BASE}/medecin/me/password`, {
        method: "PUT",
        headers,
        body: JSON.stringify({
          mot_de_passe_actuel: pwd.mot_de_passe_actuel,
          nouveau_mot_de_passe: pwd.nouveau_mot_de_passe,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.msg || "Erreur changement mot de passe");

      flashSuccess("Mot de passe modifié");
      closePwd();
    } catch (e) {
      setError(e?.message || "Impossible de contacter le serveur.");
    } finally {
      setLoadingPwd(false);
    }
  };

  return (
    <div className="adminPanel blocagesCompact cabinetCompact">
      {error && <div className="adminError">{error}</div>}
      {success && <div className="adminSuccess">{success}</div>}

      {/* =========================
          INFORMATIONS CABINET
         ========================= */}
      <div className="adminCard adminCardSpan">
        <div className="adminCardHeader cabinetCardHeader">
          <div>
            <div className="adminCardTitle">Informations du cabinet</div>
            <div className="adminCardDesc">Nom et prénom ne sont pas modifiables</div>
          </div>


          <button
          className="btnSecondary"
          onClick={async () => {
            await fetchMe();
            showToast("success", "Informations à jour");
          }}
          disabled={loading}
        >
            {loading ? "Chargement..." : "Rafraîchir"}
          </button>

        </div>

        <div className="cabinetForm">
          <div className="cabinetGrid2">
            <label className="cabinetField">
              <div className="cabinetLabel">Prénom</div>
              <input className="cabinetInput cabinetReadonly" value={profile.prenom} disabled />
            </label>

            <label className="cabinetField">
              <div className="cabinetLabel">Nom</div>
              <input className="cabinetInput cabinetReadonly" value={profile.nom} disabled />
            </label>
          </div>

          <div className="cabinetGrid2">
            <label className="cabinetField">
              <div className="cabinetLabel">Adresse email</div>
              <input
                className="cabinetInput"
                type="email"
                value={form.email}
                onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))}
                placeholder="ex: medecin@mail.com"
                autoComplete="email"
              />
            </label>

            <label className="cabinetField">
              <div className="cabinetLabel">Téléphone du cabinet</div>
              <input
                className="cabinetInput"
                value={form.telephone}
                onChange={(e) => setForm((s) => ({ ...s, telephone: e.target.value }))}
                placeholder="ex: 06 12 34 56 78"
                autoComplete="tel"
              />
            </label>
          </div>

          {/* ✅ Adresse + CP + Ville */}
          <label className="cabinetField">
            <div className="cabinetLabel">Adresse</div>
            <input
              className="cabinetInput"
              value={form.adresse_cabinet}
              onChange={(e) => setForm((s) => ({ ...s, adresse_cabinet: e.target.value }))}
              placeholder="ex: 10 rue de la Paix"
              autoComplete="street-address"
            />
          </label>

          <div className="cabinetGrid2">
            <label className="cabinetField">
              <div className="cabinetLabel">Code postal</div>
              <input
                className="cabinetInput"
                inputMode="numeric"
                value={form.code_postal_cabinet}
                onChange={(e) =>
                  setForm((s) => ({ ...s, code_postal_cabinet: e.target.value.replace(/\D/g, "").slice(0, 5) }))
                }
                placeholder="ex: 75002"
                autoComplete="postal-code"
              />
            </label>

            <label className="cabinetField">
              <div className="cabinetLabel">Ville</div>
              <input
                className="cabinetInput"
                value={form.ville_cabinet}
                onChange={(e) => setForm((s) => ({ ...s, ville_cabinet: e.target.value }))}
                placeholder="ex: Paris"
                autoComplete="address-level2"
              />
            </label>
          </div>

          <div className="cabinetActions">
            <button className="btnSecondary btnCompact" onClick={cancelProfileEdits} disabled={loading || !isDirty}>
              Annuler
            </button>

            <button className="btnPrimary btnCompact" onClick={requestSave} disabled={loading || !isDirty}>
              Enregistrer
            </button>
          </div>

          {/* ✅ Confirmation mdp */}
          {confirmSaveOpen && (
            <div className="cabinetConfirmBox">
              <div className="cabinetConfirmTitle">Confirmer votre identité</div>
              <div className="cabinetConfirmSub">Pour valider ces modifications, saisissez votre mot de passe.</div>

              <label className="cabinetField" style={{ marginTop: 10 }}>
                <div className="cabinetLabel">Mot de passe</div>
                <input
                  ref={confirmPwdRef}
                  className="cabinetInput"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Votre mot de passe actuel"
                  autoComplete="current-password"
                />
              </label>

              <div className="cabinetActions" style={{ marginTop: 10 }}>
                <button
                  className="btnSecondary btnCompact"
                  onClick={() => {
                    setConfirmSaveOpen(false);
                    setConfirmPassword("");
                    resetMessages();
                  }}
                  disabled={loading}
                >
                  Annuler
                </button>

                <button className="btnPrimary btnCompact" onClick={confirmAndSaveProfile} disabled={loading}>
                  {loading ? "Validation..." : "Valider & enregistrer"}
                </button>
              </div>
            </div>
          )}

          {!isDirty && !confirmSaveOpen && <div className="cabinetHint">Les informations affichées sont à jour.</div>}
        </div>
      </div>

      {/* =========================
          SÉCURITÉ
         ========================= */}
      <div className="adminCard adminCardSpan">
        <div className="adminCardHeader cabinetCardHeader">
          <div>
            <div className="adminCardTitle">Sécurité</div>
            <div className="adminCardDesc">Changez votre mot de passe</div>
          </div>

          {!showPasswordForm ? (
            <button
              className="btnPrimary btnCompact"
              onClick={() => {
                resetMessages();
                setShowPasswordForm(true);
              }}
            >
              Changer le mot de passe
            </button>
          ) : (
            <button className="btnSecondary btnCompact" onClick={closePwd} disabled={loadingPwd}>
              Fermer
            </button>
          )}
        </div>

        <div className={`cabinetCollapse ${showPasswordForm ? "isOpen" : ""}`}>
          <div className="cabinetPwdGrid">
            <label className="cabinetField">
              <div className="cabinetLabel">Nouveau mot de passe</div>
              <input
                className="cabinetInput"
                type="password"
                value={pwd.nouveau_mot_de_passe}
                onChange={(e) => setPwd((s) => ({ ...s, nouveau_mot_de_passe: e.target.value }))}
                placeholder="Minimum 8 caractères"
                autoComplete="new-password"
              />
            </label>

            <label className="cabinetField">
              <div className="cabinetLabel">Confirmation</div>
              <input
                className="cabinetInput"
                type="password"
                value={pwd.confirmation}
                onChange={(e) => setPwd((s) => ({ ...s, confirmation: e.target.value }))}
                placeholder="Retapez le nouveau mot de passe"
                autoComplete="new-password"
              />
            </label>

            <label className="cabinetField">
              <div className="cabinetLabel">Mot de passe actuel</div>
              <input
                className="cabinetInput"
                type="password"
                value={pwd.mot_de_passe_actuel}
                onChange={(e) => setPwd((s) => ({ ...s, mot_de_passe_actuel: e.target.value }))}
                placeholder="Votre mot de passe actuel"
                autoComplete="current-password"
              />
            </label>
          </div>

          <div className="cabinetActions">
            <button className="btnSecondary btnCompact" onClick={closePwd} disabled={loadingPwd}>
              Annuler
            </button>

            <button className="btnPrimary btnCompact" onClick={changePassword} disabled={loadingPwd}>
              {loadingPwd ? "Modification..." : "Enregistrer"}
            </button>
          </div>

          <div className="cabinetHint">Conseil : utilisez au moins 8 caractères, avec lettres et chiffres.</div>
        </div>
      </div>
      {toast.msg && (
  <div className={`adminToast ${toast.type}`}>
    {toast.msg}
  </div>
)}
    </div>
  );
}
